import * as React from 'react'
import { get } from 'lodash'
import { FormEvent, ReactElement, useEffect, useState } from 'react'
import { FormProps, TouchedFields, useFormContext, ValidatedForm } from 'remix-validated-form'

import { cn } from 'app/lib/utils'
import { Button } from './button'
import { trace } from 'lib/logging'

const NOOP = () => {}
const GO_BACK = () => history.back()

export enum SubAction {
  CREATE = 'create',
  EDIT = 'edit',
  DELETE = 'delete',
  ARCHIVE = 'archive',
}

export interface SmartFormProps<
  DataType extends {
    [fieldName: string]: any
  },
> extends Omit<FormProps<DataType, any>, 'className' | 'onSubmit' | 'formRef'> {
  formRef?: React.RefObject<HTMLFormElement | null>
  id?: string
  className?: string
  showDelete?: boolean
  hideButtons?: boolean
  shouldPreventDefault?: boolean
  hiddenInputs?: { name: string; value?: string | string[] }[]
  preventSubmit?: boolean
  addDefaultClass?: boolean
  formClassName?: string
  onTouchedFields?: (touched: boolean, touchedFields?: TouchedFields) => void
  onChange?: (data?: FormEvent<HTMLFormElement>) => void
  onCancel?: () => void
  onDelete?: () => void
  onPreventDefault?: (args?: any) => void
  onSubmit?: (data?: DataType) => void
  onValidationError?: (errors: Record<string, string>) => void
}

/**
 * Inner component that lives inside ValidatedForm's FormContext.Provider.
 * It reactively observes touchedFields via Context (not formRegistry),
 * then notifies SmartForm (the parent) of dirty state changes.
 */
function DirtyFieldsObserver({
  defaultValues,
  onFieldsChange,
}: {
  defaultValues?: Record<string, unknown>
  onFieldsChange?: (touched: boolean, touchedFields?: TouchedFields) => void
}): ReactElement | null {
  const formContext = useFormContext()

  useEffect(() => {
    if (!onFieldsChange) return
    const fieldsChanged = Object.entries(formContext.touchedFields).filter(([k, v]) => {
      if (!v) return false
      const defaultValue = get(defaultValues, k) ?? ''

      if (typeof defaultValue === 'object' && defaultValue !== null) {
        return true
      }

      const formValues = formContext.getValues()
      return (
        (formValues.has(k) && `${defaultValue}` !== formValues.get(k)) ||
        (formValues.has(`${k}[value]`) && `${defaultValue}` !== formValues.get(`${k}[value]`)) ||
        !formValues.has(k)
      )
    })
    onFieldsChange(fieldsChanged.length > 0, formContext.touchedFields)
  }, [formContext.touchedFields])

  return null
}

function SmartForm<
  DataType extends {
    [fieldName: string]: any
  },
>({
  id = 'smartForm',
  method = 'post',
  className = '',
  encType = 'application/x-www-form-urlencoded',
  replace = true,
  showDelete = false,
  hideButtons = false,
  shouldPreventDefault = false,
  validator,
  defaultValues,
  hiddenInputs,
  children,
  subaction,
  preventSubmit = false,
  formClassName,
  addDefaultClass = false,
  onCancel = GO_BACK,
  onTouchedFields: onFieldsChange,
  onChange,
  onDelete = NOOP,
  onPreventDefault = NOOP,
  onSubmit,
  onValidationError,
  action,
  formRef,
}: SmartFormProps<DataType>) {
  const formContext = useFormContext(id)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [preventDefaultData, setPreventDefaultData] = useState<any>(undefined)

  const getHiddenInputErrors = () => {
    return Object.entries(formContext.fieldErrors).filter(
      ([name, error]) => error && hiddenInputs?.find((hidden) => hidden.name === name),
    )
  }

  const handleSubmit = (data: DataType, event: React.FormEvent<HTMLFormElement>) => {
    trace('form', 'handleSubmit', id, data, preventSubmit)
    if (preventSubmit) {
      setIsSubmitting(true)
      event.preventDefault()
      event.stopPropagation()
    }
    if (shouldPreventDefault && action) {
      event.preventDefault()
      event.stopPropagation()
      const formData = new FormData(event.currentTarget)
      window
        .fetch(action, {
          method: method || 'post',
          credentials: 'include',
          body: formData,
        })
        .then((r) => r.json())
        .then((d) => {
          setPreventDefaultData(d)
          onPreventDefault?.(d)
        })
        .catch(() => {})
    }
    onSubmit?.(data)
  }

  useEffect(() => {
    if (shouldPreventDefault && preventDefaultData) {
      onPreventDefault?.(preventDefaultData)
    }
  }, [preventDefaultData])

  useEffect(() => {
    if (Object.entries(formContext.fieldErrors).length) {
      // eslint-disable-next-line no-console
      console.error('Form errors', id, formContext.fieldErrors)
      onValidationError?.(formContext.fieldErrors)
    }
  }, [formContext.fieldErrors])

  return (
    <ValidatedForm
      encType={encType}
      replace={replace}
      validator={validator}
      defaultValues={defaultValues}
      subaction={subaction}
      method={method}
      action={action}
      id={id}
      autoComplete="off"
      fetcher={undefined}
      onSubmit={handleSubmit}
      onChange={(val) => onChange?.(val)}
      className={cn(formClassName, addDefaultClass && 'flex grow overflow-hidden')}
      formRef={formRef}
    >
      <DirtyFieldsObserver defaultValues={defaultValues} onFieldsChange={onFieldsChange} />
      <div className={cn('mt-6 grid max-w-3xl grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-6', className)}>
        {hiddenInputs?.map((hiddenInput, i) => (
          <input key={i} type="hidden" name={hiddenInput.name} value={hiddenInput.value} />
        ))}
        {children}
        {getHiddenInputErrors().map(([k, error], i) => (
          <p key={`${k}-${i}`} className="col-span-6 mt-4 text-red-500">
            {error}
          </p>
        ))}
        {!hideButtons && (
          <div className="sticky bottom-4 z-10 col-span-6 my-4 bg-surface">
            <div className="flex justify-end gap-4">
              <Button type="button" variant="white" size="md" onClick={onCancel}>
                Cancel
              </Button>
              {showDelete && (
                <Button type="button" variant="secondary" size="md" onClick={() => onDelete()}>
                  Delete
                </Button>
              )}
              <Button type="submit" variant="primary" size="md" loading={isSubmitting}>
                Save
              </Button>
            </div>
          </div>
        )}
      </div>
    </ValidatedForm>
  )
}

export { SmartForm }
